import { DateTime } from "luxon";

const initialState = {
  user: null,
  notaryJobs: null,
  notaryRequests: [],
  realTimeRequestsFetched: false,
  expiredRequestsFetched: false,
  lastWebSocketMessage: null,
  navigateToRealRequestsTab: false,
  requestCountdowns: {}, // Store countdown timers for each request
};

const getRequestCountdownSeconds = (request) => {
  if (!request) return 0;

  const nowMs = Date.now();

  let expiresAtMs = Date.parse(request.expires_at || "");

  if (Number.isNaN(expiresAtMs)) {
    const createdAtMs = Date.parse(request.created_at || "");
    if (!Number.isNaN(createdAtMs)) {
      expiresAtMs = createdAtMs + 5 * 60 * 1000;
    }
  }

  if (Number.isNaN(expiresAtMs)) return 0;

  return Math.max(0, Math.floor((expiresAtMs - nowMs) / 1000));
};

const isSameRealTimeRequestIdentity = (a, b) => {
  if (!a || !b) return false;

  if (a.id && b.id && a.id === b.id) {
    return true;
  }

  return (
    !!a.task_id &&
    !!b.task_id &&
    !!a.signer &&
    !!b.signer &&
    a.task_id === b.task_id &&
    a.signer === b.signer
  );
};

const dedupeRealTimeRequests = (requests) => {
  const deduped = [];

  (Array.isArray(requests) ? requests : []).forEach((request) => {
    const existingIndex = deduped.findIndex((item) =>
      isSameRealTimeRequestIdentity(item, request),
    );

    if (existingIndex === -1) {
      deduped.push(request);
    } else {
      // Keep the latest payload for the same logical request identity
      deduped[existingIndex] = request;
    }
  });

  return deduped;
};

const notarySessionReducer = (state = initialState, action) => {
  switch (action.type) {
    case "DISMISS_RON_REQUEST":
      const dismissedRequestId = action.payload;
      const filteredRequests = state.notaryRequests.filter(
        (request) => request.id !== dismissedRequestId,
      );
      const countdownsAfterDismiss = { ...state.requestCountdowns };
      delete countdownsAfterDismiss[dismissedRequestId];
      return {
        ...state,
        notaryRequests: filteredRequests,
        requestCountdowns: countdownsAfterDismiss,
      };
    case "DISMISS_ALL_RON_REQUEST":
      return {
        ...state,
        notaryRequests: [],
        requestCountdowns: {},
      };
    case "NAVIGATE_TO_REAL_REQUESTS_TAB":
      return {
        ...state,
        navigateToRealRequestsTab: true,
      };
    case "CLEAR_NAVIGATE_TO_REAL_REQUESTS_TAB":
      return {
        ...state,
        navigateToRealRequestsTab: false,
      };
    case "ADD_REAL_TIME_REQUEST": {
      const newRequest = action.payload;
      const countdownTime = getRequestCountdownSeconds(newRequest);

      // Replace any existing request representing the same logical request
      // (same id OR same task_id+signer).
      const requestsToRemove = state.notaryRequests.filter((request) =>
        isSameRealTimeRequestIdentity(request, newRequest),
      );

      const updatedRequests = [
        ...state.notaryRequests.filter(
          (request) => !isSameRealTimeRequestIdentity(request, newRequest),
        ),
        newRequest,
      ];

      const nextCountdowns = { ...state.requestCountdowns };
      requestsToRemove.forEach((request) => {
        if (request?.id) {
          delete nextCountdowns[request.id];
        }
      });
      if (newRequest?.id) {
        nextCountdowns[newRequest.id] = countdownTime;
      }

      return {
        ...state,
        notaryRequests: updatedRequests,
        requestCountdowns: nextCountdowns,
      };
    }
    case "SET_REAL_TIME_REQUESTS": {
      const dedupedRequests = dedupeRealTimeRequests(action.payload);
      const nextCountdowns = {};

      dedupedRequests.forEach((request) => {
        if (request?.id) {
          nextCountdowns[request.id] = getRequestCountdownSeconds(request);
        }
      });

      return {
        ...state,
        notaryRequests: dedupedRequests,
        requestCountdowns: nextCountdowns,
      };
    }
    case "UPDATE_REQUEST_COUNTDOWN":
      const { requestId, timeRemaining } = action.payload;
      return {
        ...state,
        requestCountdowns: {
          ...state.requestCountdowns,
          [requestId]: timeRemaining,
        },
      };
    case "REMOVE_EXPIRED_REQUEST":
      const expiredRequestId = action.payload;
      const updatedCountdowns = { ...state.requestCountdowns };
      delete updatedCountdowns[expiredRequestId];
      return {
        ...state,
        notaryRequests: state.notaryRequests.filter(
          (request) => request.id !== expiredRequestId,
        ),
        requestCountdowns: updatedCountdowns,
      };
    case "SET_REAL_TIME_REQUESTS_FETCHED":
      return {
        ...state,
        realTimeRequestsFetched: !!action.payload,
      };
    case "SET_EXPIRED_REQUESTS_FETCHED":
      return {
        ...state,
        expiredRequestsFetched: !!action.payload,
      };
    case "ADD_NOTARY_USER_DATA":
      return {
        ...state,
        user: action.payload.data,
        notaryJobs: action.payload.jobs,
        realTimeRequestsFetched: false,
        expiredRequestsFetched: false,
      };
    case "UPDATE_NOTARY_USER_DATA":
      return {
        ...state,
        user: action.payload.user,
        notaryJobs: action.payload.jobs,
      };
    case "SET_EXPIRED_EXPEDITED_REQUESTS":
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              expired_expedited_requests: action.payload,
              expired_count: Array.isArray(action.payload)
                ? action.payload.length
                : 0,
            }
          : state.user,
      };
    case "UPDATE_NOTARY_JOBS":
      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          jobs_list: action.payload.jobs_list,
          accepted_job_list: action.payload.accepted_job_list,
        },
      };
    case "ADD_JOB_TO_QUEUE": //! BROADCAST
      const job = action.payload;
      // Assuming `ronDateRaw` always contains at least one element and the required fields.
      const rawDate = job.ronDateRaw[0];

      // Create a UTC DateTime object from the raw date information
      const utcDate = DateTime.utc(
        rawDate.year,
        rawDate.month,
        rawDate.day_of_month,
        parseInt(rawDate.time.split(":")[0]),
        parseInt(rawDate.time.split(":")[1]),
      );

      // Get the user's timezone from the state or default to America/New_York if not available
      const userTimezone = state.user?.timezone || "America/New_York";

      // Convert UTC DateTime to the user's timezone using the `signee_timezone` field
      const localDate = utcDate.setZone(userTimezone);

      // Format the localDate to the desired string format "April 22nd 2024, 10:30 PM"
      const formattedDate = localDate.toFormat("MMMM dd'th' yyyy, h:mm a");

      // Update the job object with the new date string
      const updatedJob = {
        ...job,
        ronDateString: formattedDate,
      };

      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          jobs_list: [updatedJob, ...state.notaryJobs.jobs_list],
        },
      };
    case "REMOVE_JOB_FROM_QUEUE": //! BROADCAST
      const jobIdToRemove = action.payload.id;
      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          // Filter out the job that matches the ID to remove
          jobs_list: state.notaryJobs.jobs_list.filter(
            (job) => job.id !== jobIdToRemove,
          ),
        },
      };
    case "ADD_ACCEPTED_QUEUE_JOB": //! PERSONAL
      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          accepted_job_list: [
            action.payload,
            ...state.notaryJobs.accepted_job_list,
          ],
        },
      };
    case "REMOVE_ACCEPTED_QUEUE_JOB": //! PERSONAL
      const acceptedIdToRemove = action.payload.id; // Assuming each job has a unique 'id' field
      return {
        ...state,
        notaryJobs: {
          ...state.notaryJobs,
          // Filter out the job that matches the ID to remove
          accepted_job_list: state.notaryJobs.accepted_job_list.filter(
            (job) => job.id !== acceptedIdToRemove,
          ),
        },
      };
    case "STORE_WEBSOCKET_MESSAGE":
      return {
        ...state,
        lastWebSocketMessage: action.payload,
      };
    //! //////////////////////////////////////////////////////////////////////////////////
    default:
      return state;
  }
};

export default notarySessionReducer;
